import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import type { VariableSizeList as List } from 'react-window';
import type { Row } from 'react-table';

import { BringgReactTable } from 'bringg-web/components/bringg-react-table';
import ColumnsButton from '../table-customization/columns-button';
import GroupByDropdown from '../table-customization/group-by-dropdown/group-by-dropdown';
import FiltersPanel from '../table-customization/filters-panel/filters-panel';
import BatchDismiss from '../batch-dismiss/batch-dismiss';
import { OwnedAlertsPanel } from '../owned-alerts-panel';
import { DismissConfirmContext } from '../dismiss-confirm';
import InViewRow from '../in-view-row/in-view-row';
import { useStores } from '../../../recipes';
import useControlledTableState from '../hooks/use-controlled-table-state';
import useInitialTableState from '../hooks/use-initial-table-state';
import useTableStateReducer from '../hooks/use-table-state-reducer';
import useAlertsTable from '../hooks/use-alerts-table';
import useRenderTableContainer from '../hooks/use-render-table-container';
import getFilterTypes from '../get-filter-types';
import useFilteredAlertColumns from 'bringg-web/features/exceptions-management/hooks/use-filtered-alert-columns';
import { columns as allColumns } from '../columns/driver-alert-columns';
import { getGroupByOptionsByTab, getRowSelectableState } from '../utils';
import { defaultExceptionsTableRowHeight } from '../../../consts';
import { PresetViewsPickerContainer } from 'bringg-web/features/preset-views/preset-views-picker';
import { DRIVER_ALERTS_TABLE_KEY } from 'bringg-web/features/preset-views/utils';
import type { ElasticAlert } from '../../../types/common.consts';
import { useTableLayoutEffect } from '../hooks/use-table-layout-effect';
import usePresetViewStateChanged from '../hooks/use-preset-view-state-changed';

const tableDOMId = 'driver-exceptions-table';

interface Props {
	presetViewsEnabled: boolean;
}

export const DriverAlertsTab = observer(({ presetViewsEnabled }: Props) => {
	const { t } = useTranslation();
	const listRef = useRef<List>(null);

	const {
		alertsStore: { allDriverAlerts, isAllAlertsLoaded, alertTypesFilteredByConfig },
		alertsViewStore,
		usersStore: { currentUser }
	} = useStores();
	const { setDismissModalVisible, setIdsToDismiss } = useContext(DismissConfirmContext);

	const onBatchDismiss = useCallback(
		(rowIds: string[]) => {
			setDismissModalVisible(true);
			setIdsToDismiss(rowIds);
		},
		[setDismissModalVisible, setIdsToDismiss]
	);

	// TODO extact to a customHook
	const [tableContainerNode, setTableContainerNode] = useState<HTMLDivElement>(null);
	const tableContainerCbRef = useCallback(node => {
		if (node !== null) {
			setTableContainerNode(node);
		}
	}, []);

	const columns = useFilteredAlertColumns(allColumns);
	const filterTypes = useMemo(() => getFilterTypes<ElasticAlert>(), []);
	const useControlledState = useControlledTableState<ElasticAlert>(alertsViewStore.driverAlertsTableConfig);
	const initialState = useInitialTableState<ElasticAlert>(alertsViewStore.driverAlertsTableConfig);
	const stateReducer = useTableStateReducer<ElasticAlert>(alertsViewStore.driverAlertsTableConfig);
	const getRowSelectable = useCallback(
		(row: Row<ElasticAlert>) => getRowSelectableState(row, currentUser.id),
		[currentUser]
	);

	const tableInstance = useAlertsTable<ElasticAlert>({
		data: allDriverAlerts,
		columns,
		initialState,
		useControlledState,
		stateReducer,
		filterTypes,
		autoResetState: false,
		getRowSelectable
	});

	const { state, toggleAllRowsExpanded } = tableInstance;
	// Expand all grouped rows after groupByColumns is changed
	useEffect(() => {
		if (state.groupBy.length) {
			toggleAllRowsExpanded(true);
		}
	}, [state.groupBy, toggleAllRowsExpanded]);

	const getItemSize = useCallback((): number => defaultExceptionsTableRowHeight, []);
	const TableContainerComponent = useRenderTableContainer<ElasticAlert>(listRef, getItemSize, !isAllAlertsLoaded);

	const groupByOptions = useMemo(() => getGroupByOptionsByTab('driverAlerts', t), [t]);

	const { isCustomizeListLayoutAllowed, tableGroupBy, setTableGroupBy, tableColumnOrder, hiddenTableColumnsSet } =
		alertsViewStore.driverAlertsTableConfig;

	useTableLayoutEffect({
		tableContainerNode,
		tableInstance,
		columns,
		tableColumnOrder,
		hiddenTableColumnsSet,
		presetViewsEnabled
	});

	usePresetViewStateChanged<ElasticAlert>(alertsViewStore.driverAlertsTableConfig, tableInstance);

	return (
		<div className="exceptions-page">
			<FiltersPanel
				alertTypes={alertTypesFilteredByConfig.driverAlerts}
				tableConfig={alertsViewStore.driverAlertsTableConfig}
			/>
			<div className="alert-tabs-table-info">
				<OwnedAlertsPanel />
				<BatchDismiss selectedRowIds={state.selectedRowIds} onDismiss={onBatchDismiss} />
				<div className="customization-group">
					<GroupByDropdown options={groupByOptions} groupBy={tableGroupBy} setGroupBy={setTableGroupBy} />
					{presetViewsEnabled ? (
						<PresetViewsPickerContainer
							// TODO: rename `path` in PresetViewsPickerContainer to `viewId` to be less specific(like path)
							path={DRIVER_ALERTS_TABLE_KEY}
							className="alerts-preset-view-picker"
						/>
					) : null}
					{isCustomizeListLayoutAllowed && (
						<ColumnsButton tableConfig={alertsViewStore.driverAlertsTableConfig} />
					)}
				</div>
			</div>

			<BringgReactTable<ElasticAlert>
				id={tableDOMId}
				tableContainerCbRef={tableContainerCbRef}
				className="exceptions-table-wrapper"
				tableInstance={tableInstance}
				TableContainerComponent={TableContainerComponent}
				RenderRow={InViewRow}
			/>
		</div>
	);
});
